<template>
  <div class="page px-6 py-4">
    <div class="d-flex align-start justify-space-between flex-wrap mb-4">
      <div>
        <h1 class="text-h5 font-weight-bold mb-1">Календарь услуг</h1>
        <div class="text-body-2 grey--text text--darken-1">
          Заводите интервалы доступности экспертов и обрабатывайте записи клиентов.
        </div>
      </div>
      <div class="d-flex mt-2 mt-sm-0">
        <div class="calendar-stat mr-6">
          <div class="calendar-stat__value">{{ monthSummary.openSlots }}</div>
          <div class="calendar-stat__label">Открытых интервалов</div>
        </div>
        <div class="calendar-stat mr-6">
          <div class="calendar-stat__value">{{ monthSummary.appointments }}</div>
          <div class="calendar-stat__label">Записей в месяце</div>
        </div>
        <div class="calendar-stat">
          <div class="calendar-stat__value">{{ monthSummary.pending }}</div>
          <div class="calendar-stat__label">Ожидают подтверждения</div>
        </div>
      </div>
    </div>

    <v-tabs v-model="tab" color="#483CB9" class="mb-4 calendar-tabs">
      <v-tab>Календарь и слоты</v-tab>
      <v-tab>Команда</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab">
      <v-tab-item>
        <v-row>
          <v-col cols="12" md="7">
            <MonthGrid
              :month="currentMonth"
              :selected-date="selectedDate"
              :availability="availabilityForMonth(currentMonth)"
              :appointments="appointmentsForMonth(currentMonth)"
              @update:month="setCurrentMonth"
              @select-date="setSelectedDate"
            />
          </v-col>
          <v-col cols="12" md="5">
            <DaySlotsPanel
              :date="selectedDate"
              :availability="availabilityByDate(selectedDate)"
              :appointments="appointmentsByDate(selectedDate)"
              :cancelled-appointments="cancelledAppointmentsByDate(selectedDate)"
              :experts="experts"
              :services="services"
              :service-types="serviceTypes"
              :action-loading="loading.action"
              @add="openCreateAvailability"
              @edit-availability="openEditAvailability"
              @close-availability="onCloseAvailability"
              @accept-appointment="onAcceptAppointment"
              @decline-appointment="onDeclineAppointment"
              @edit-conference-link="onEditConferenceLink"
            />
          </v-col>
        </v-row>
      </v-tab-item>

      <v-tab-item>
        <ExpertsList
          :experts="experts"
          :availability="openAvailability"
          :services="services"
          :service-types="serviceTypes"
          @add="openCreateExpert"
          @edit="openEditExpert"
          @delete="onDeleteExpert"
        />

        <v-row class="mt-4">
          <v-col cols="12" md="6">
            <ServiceTypesManager
              :service-types="serviceTypes"
              :loading="loading.action"
              @save="onSaveServiceType"
              @toggle-active="onToggleServiceTypeActive"
            />
          </v-col>
        </v-row>
      </v-tab-item>
    </v-tabs-items>

    <AvailabilityFormDialog
      v-model="availabilityDialog.show"
      :editing-availability="editingAvailability"
      :default-date="selectedDate"
      :experts="experts"
      :services="services"
      :service-types="serviceTypes"
      :loading="loading.action"
      @save="onSaveAvailability"
    />

    <ExpertFormDialog
      v-model="expertDialog.show"
      :editing-expert="expertDialog.expert"
      :services="expertDialog.expert ? servicesByExpert(expertDialog.expert.id) : []"
      :service-types="serviceTypes"
      :loading="loading.action"
      @save="onSaveExpert"
      @upload-photo="onUploadExpertPhoto"
      @delete-photo="onDeleteExpertPhoto"
    />

    <StatusModal
      :show="notification.show"
      :success="notification.success"
      :message="notification.message"
      @update:show="SET_NOTIFICATION({ show: $event, success: notification.success, message: notification.message })"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import MonthGrid from '@/components/Calendar/Admin/MonthGrid.vue';
import DaySlotsPanel from '@/components/Calendar/Admin/DaySlotsPanel.vue';
import AvailabilityFormDialog from '@/components/Calendar/Admin/AvailabilityFormDialog.vue';
import ExpertsList from '@/components/Calendar/Admin/ExpertsList.vue';
import ExpertFormDialog from '@/components/Calendar/Admin/ExpertFormDialog.vue';
import ServiceTypesManager from '@/components/Calendar/Admin/ServiceTypesManager.vue';
import StatusModal from '@/components/common/StatusModal.vue';

export default {
  name: 'Calendar',
  components: {
    MonthGrid,
    DaySlotsPanel,
    AvailabilityFormDialog,
    ExpertsList,
    ExpertFormDialog,
    ServiceTypesManager,
    StatusModal,
  },
  data() {
    return {
      tab: 0,
      expertDialog: {
        show: false,
        expert: null,
      },
    };
  },
  computed: {
    ...mapGetters('calendar', [
      'experts',
      'serviceTypes',
      'services',
      'loading',
      'notification',
      'currentMonth',
      'selectedDate',
      'availabilityDialog',
      'availabilityByDate',
      'availabilityForMonth',
      'appointmentsByDate',
      'cancelledAppointmentsByDate',
      'appointmentsForMonth',
      'openAvailability',
      'servicesByExpert',
    ]),
    monthSummary() {
      return this.$store.getters['calendar/monthSummary'](this.currentMonth);
    },
    editingAvailability() {
      if (!this.availabilityDialog.id) return null;
      return this.openAvailability.find((item) => item.id === this.availabilityDialog.id) || null;
    },
  },
  created() {
    this.loadCalendarData();
  },
  methods: {
    ...mapActions('calendar', [
      'loadCalendarData',
      'createAvailability',
      'updateAvailability',
      'closeAvailability',
      'acceptAppointment',
      'declineAppointment',
      'setConferenceLink',
      'setSelectedDate',
      'setCurrentMonth',
      'openCreateAvailability',
      'openEditAvailability',
      'closeAvailabilityDialog',
      'saveExpertWithServices',
      'deleteExpert',
      'uploadExpertPhoto',
      'deleteExpertPhoto',
      'createServiceType',
      'updateServiceType',
      'setServiceTypeActive',
    ]),
    ...mapMutations('calendar', ['SET_NOTIFICATION']),
    async onSaveAvailability(payload) {
      if (payload.id) {
        await this.updateAvailability(payload);
      } else {
        await this.createAvailability(payload);
      }
      this.closeAvailabilityDialog();
    },
    async onCloseAvailability(id) {
      await this.closeAvailability(id);
    },
    async onAcceptAppointment(payload) {
      await this.acceptAppointment(payload);
    },
    async onDeclineAppointment(id) {
      // eslint-disable-next-line no-alert
      if (!window.confirm('Отклонить эту запись?')) return;
      await this.declineAppointment(id);
    },
    async onEditConferenceLink(appointment) {
      // eslint-disable-next-line no-alert
      const url = window.prompt('Ссылка на конференцию', appointment.conference_url || '');
      if (!url) return;
      await this.setConferenceLink({ id: appointment.id, conference_url: url });
    },
    openCreateExpert() {
      this.expertDialog = { show: true, expert: null };
    },
    openEditExpert(expert) {
      this.expertDialog = { show: true, expert };
    },
    async onSaveExpert(payload) {
      await this.saveExpertWithServices(payload);
      this.expertDialog.show = false;
    },
    async onDeleteExpert(id) {
      await this.deleteExpert(id);
    },
    async onUploadExpertPhoto(payload) {
      await this.uploadExpertPhoto(payload);
      this.refreshExpertDialog(payload.id);
    },
    async onDeleteExpertPhoto(id) {
      await this.deleteExpertPhoto(id);
      this.refreshExpertDialog(id);
    },
    refreshExpertDialog(expertId) {
      if (!this.expertDialog.expert || this.expertDialog.expert.id !== expertId) return;
      this.expertDialog.expert = this.experts.find((item) => item.id === expertId) || null;
    },
    async onSaveServiceType(payload) {
      if (payload.id) {
        await this.updateServiceType(payload);
      } else {
        await this.createServiceType(payload);
      }
    },
    async onToggleServiceTypeActive(payload) {
      await this.setServiceTypeActive(payload);
    },
  },
};
</script>

<style lang="scss" scoped>
.calendar-tabs ::v-deep .v-tab {
  text-transform: none;
}

.calendar-stat {
  text-align: center;

  &__value {
    font-size: 22px;
    font-weight: 700;
    line-height: 1;
  }

  &__label {
    font-size: 12px;
    color: #8a8f9c;
  }
}
</style>
